import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  return (
    <section className="relative bg-background py-20 lg:py-32 overflow-hidden">
      
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.3] pointer-events-none" />
      <div className="hidden lg:block absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px] opacity-60 pointer-events-none" />
      <div className="hidden lg:block absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[100px] opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold border border-primary/20">
              <Icon name="Zap" className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Certified n8n & Make.com Partners</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight">
              Master Workflow Automation with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                n8n & Make
              </span>
            </h1>
            
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Stop settling for basic integrations. We architect complex, logic-heavy workflows that handle error management, data transformation, and API connectivity securely.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link to="/contact">
                <Button 
                  variant="default" 
                  size="lg" 
                  iconName="Calendar" 
                  iconPosition="left"
                  className="w-full sm:w-auto shadow-lg shadow-primary/25"
                >
                  Book Technical Audit
                </Button>
              </Link>
              <Link to="#examples">
                <Button 
                  variant="outline" 
                  size="lg" 
                  iconName="ArrowDown" 
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  See Examples
                </Button>
              </Link>
            </div>
            
            <div className="pt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-4 text-sm font-medium text-muted-foreground">
              <div className="flex items-center space-x-2">
                  <Icon name="Check" className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-success" />
                <span>1,000+ Integrations</span>
              </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-success" />
                  <span>Zero Data Loss</span>
                </div>
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={18} className="text-success" />
                <span>Enterprise Security</span>
              </div>
            </div>
          </div>
          
          {/* Right Visual: Workflow Simulator */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-full">
            <div className="relative bg-card rounded-2xl shadow-2xl p-6 border border-border overflow-hidden">
              
              {/* Header Bar */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Workflow" className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-sm">Lead_Processing_v2</h3>
                    <p className="text-xs text-muted-foreground">Last run: Just now</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-success/10 text-success px-2 py-1 rounded text-xs font-bold uppercase tracking-wider">
                  <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
                  <span>Active</span>
                </div>
              </div>
              
              {/* Steps Animation */}
              <div className="space-y-3 relative">
                 {/* Connection Line */}
                 <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-border -z-10" />

                {[
                  { icon: 'Mail', label: 'Webhook: New Lead', status: 'Success', color: 'text-blue-500', bg: 'bg-blue-500/10' },
                  { icon: 'Database', label: 'Filter: High Value?', status: 'Filtered', color: 'text-purple-500', bg: 'bg-purple-500/10' },
                  { icon: 'Bot', label: 'AI: Enrich Data', status: 'Processing', color: 'text-orange-500', bg: 'bg-orange-500/10' },
                  { icon: 'Send', label: 'Slack: Notify Sales', status: 'Pending', color: 'text-green-500', bg: 'bg-green-500/10' }
                ].map((step, index) => (
                  <div key={index} className="flex items-center space-x-4 p-3 bg-background rounded-xl border border-border shadow-sm relative z-10 hover:scale-[1.02] transition-transform duration-300">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${step.bg}`}>
                      <Icon name={step.icon} className={`w-5 h-5 ${step.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground text-sm">{step.label}</div>
                      <div className="text-xs text-muted-foreground">Step {index + 1}</div>
                    </div>
                    <Icon name="Check" className="w-4 h-4 text-success" />
                  </div>
                ))}
              </div>
              
              {/* Stats Footer */}
              <div className="flex items-center justify-between pt-6 mt-2">
                <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Executions</span>
                    <div className="text-xl font-bold text-foreground">14,203</div>
                </div>
                <div className="text-right">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Time Saved</span>
                    <div className="text-xl font-bold text-primary">120 hrs</div>
                </div>
              </div>

            </div>
            
            {/* Floating Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-accent rounded-2xl opacity-20 blur-xl rotate-12 animate-pulse" />
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary rounded-full opacity-20 blur-xl" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;